import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { createGroup, type NewHousehold } from '../lib/db';
import { track } from '../lib/analytics';
import { CURRENCIES, CURRENCY_CODES } from '../lib/money';
import { Icon } from '../components/Icons';
import { Note, Segmented, Sheet, useToast } from '../components/ui';
import type { CurrencyCode } from '../types';

interface Draft { name: string; members: string }

/** "Ravi & Priya" and "Ravi and Priya" both mean two adults. */
function parseMembers(s: string): NewHousehold['people'] {
  const names = s.split(/\s*&\s*|\s+and\s+|\s*,\s*/).map((x) => x.trim()).filter(Boolean);
  return (names.length ? names : ['Someone']).map((name) => ({ name, weight: 1 }));
}

export default function NewTrip({ onClose }: { onClose: () => void }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const toast = useToast();

  const myName = user?.displayName?.split(' ')[0] || 'Me';
  const [name, setName] = useState('');
  const [when, setWhen] = useState('');
  const [currency, setCurrency] = useState<CurrencyCode>('INR');
  const [households, setHouseholds] = useState<Draft[]>([
    { name: 'My family', members: myName },
    { name: '', members: '' }
  ]);
  const [saving, setSaving] = useState(false);

  const filled = households.filter((h) => h.members.trim() || h.name.trim());
  const canSave = name.trim().length > 0 && filled.length > 0 && !saving;

  function setRow(i: number, patch: Partial<Draft>) {
    setHouseholds((hs) => hs.map((h, k) => (k === i ? { ...h, ...patch } : h)));
  }

  async function save() {
    if (!user || !canSave) return;
    setSaving(true);
    try {
      const id = await createGroup(user.uid, {
        name: name.trim(),
        when: when.trim(),
        currency,
        defaultBasis: 'perPerson',
        households: filled.map((h) => ({
          name: h.name.trim() || h.members.trim() || 'Family',
          people: parseMembers(h.members)
        }))
      });
      track('trip_created', { currency, households: filled.length });
      toast(`${name.trim()} created`);
      onClose();
      nav(`/t/${id}/families`);
    } catch (e) {
      toast((e as Error).message);
      setSaving(false);
    }
  }

  return (
    <Sheet
      title="New trip"
      onClose={onClose}
      footer={
        <button className="btn" disabled={!canSave} onClick={() => void save()}>
          {saving ? 'Creating…' : 'Create trip'}
        </button>
      }
    >
      <div className="field">
        <label htmlFor="t-name">Trip name</label>
        <input id="t-name" className="inp" value={name} autoFocus
          placeholder="Wayanad in December" onChange={(e) => setName(e.target.value)} />
      </div>

      <div className="field">
        <label htmlFor="t-when">Dates</label>
        <input id="t-when" className="inp" value={when}
          placeholder="12 – 15 Dec" onChange={(e) => setWhen(e.target.value)} />
      </div>

      <div className="field">
        <label>Currency</label>
        <Segmented
          value={currency}
          onChange={setCurrency}
          options={CURRENCY_CODES.map((c) => ({ id: c, label: `${CURRENCIES[c].symbol} ${c}` }))}
        />
        <p className="muted" style={{ fontSize: 12, lineHeight: 1.5, margin: '8px 2px 0' }}>
          Every expense on this trip is recorded in this currency, and it can't be changed later.
        </p>
      </div>

      <div className="field">
        <label>Who's coming</label>
        {households.map((h, i) => (
          <div className="who-unit" key={i}>
            <div className="uh">
              <input
                className="inp" style={{ padding: '8px 11px', fontSize: 14 }}
                value={h.name}
                placeholder={i === 0 ? 'My family' : 'The Sharmas'}
                onChange={(e) => setRow(i, { name: e.target.value })}
              />
              {households.length > 1 && (
                <button
                  className="iconbtn" aria-label="Remove this family"
                  onClick={() => setHouseholds((hs) => hs.filter((_, k) => k !== i))}
                >
                  <Icon name="close" />
                </button>
              )}
            </div>
            <div style={{ marginTop: 8 }}>
              <input
                className="inp" style={{ padding: '8px 11px', fontSize: 14 }}
                value={h.members}
                placeholder="Ravi & Priya"
                onChange={(e) => setRow(i, { members: e.target.value })}
              />
            </div>
          </div>
        ))}
        <button
          className="btn ghost" style={{ marginTop: 10 }}
          onClick={() => setHouseholds((hs) => [...hs, { name: '', members: '' }])}
        >
          Add another family
        </button>
      </div>

      <div style={{ marginTop: 16 }}>
        <Note>
          One row per <b>household</b> — a couple, a family, or someone travelling alone.
          Everyone in a household shares a single balance. You can add kids and adjust
          shares on the next screen, and invite people by link once the trip exists.
        </Note>
      </div>
    </Sheet>
  );
}
